import React, {useEffect, useRef, useState} from 'react';
import {uploadImages} from '../helper/CMSApi';
import {UploadedImage} from '../helper/types';

/** DnD Kit imports */
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

interface ImageUploaderProps {
    onUpload: (images: UploadedImage[]) => void;
    initialImages?: UploadedImage[];
}

const SortableImage: React.FC<{
    image: UploadedImage;
    index: number;
    onRemove: (image: UploadedImage) => void;
}> = ({image, index, onRemove}) => {
    const uniqueId = image.id ? image.id.toString() : `image-${index}`;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: uniqueId});

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : 'auto',
        cursor: 'move',
    };

    const src = process.env.REACT_APP_STRAPI_URL
        ? process.env.REACT_APP_STRAPI_URL + image.url
        : image.url;

    return (
        <div>
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                className="relative border p-2 bg-gray-700"
            >
                <img
                    src={src}
                    alt={`Uploaded-${index}`}
                    className="object-cover w-full h-48"
                />
                <button
                    type="button"
                    className="absolute top-1 left-1 bg-red-600 text-white text-xs px-1 rounded"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(image);
                    }}
                >
                    X
                </button>
            </div>
        </div>

    );
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
                                                         onUpload,
                                                         initialImages = [],
                                                     }) => {
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>(initialImages);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setUploadedImages(initialImages || []);
    }, [initialImages]);

    // DnD kit sensors:
    // - PointerSensor for mouse dragging
    // - KeyboardSensor for keyboard-based dragging (with sortableKeyboardCoordinates)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 5}
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setFiles(Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
    };

    /** Upload selected files to the server. */
    const uploadFiles = async () => {
        if (files.length === 0) return;
        setLoading(true);

        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('files', file));
            const uploaded: UploadedImage[] = await uploadImages(formData);

            const newImages = [...uploadedImages, ...uploaded];
            setUploadedImages(newImages);
            onUpload(newImages); // notify parent
        } catch (error) {
            console.error('Error uploading files', error);
        } finally {
            setLoading(false);
            setFiles([]);
        }
    };

    /**
     * Called after a drag operation ends. We reorder `uploadedImages` array accordingly.
     */
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;

        // Find the old/new indices
        const oldIndex = uploadedImages.findIndex((img, idx) => {
            return (img.id && img.id.toString() === active.id) || `image-${idx}` === active.id;
        });
        const newIndex = uploadedImages.findIndex((img, idx) => {
            return (img.id && img.id.toString() === over.id) || `image-${idx}` === over.id;
        });

        if (oldIndex !== -1 && newIndex !== -1) {
            const newOrder = arrayMove(uploadedImages, oldIndex, newIndex);
            setUploadedImages(newOrder);
            onUpload(newOrder);
        }
    };

    /**
     * Remove an image from the current list.
     */
    const handleRemoveImage = (image: UploadedImage) => {
        // If the image has an id, use that; otherwise, fallback to url
        const newImages = uploadedImages.filter((img) => {
            if (img.id && image.id) return img.id !== image.id;
            // fallback comparison if there's no ID
            return img.url !== image.url;
        });

        setUploadedImages(newImages);
        onUpload(newImages);
    };

    return (
        <div>
            {/* The upload drag area */}
            <div
                className={`border-2 border-dashed rounded p-6 text-center cursor-pointer ${
                    dragActive ? 'bg-gray-600 border-blue-400' : 'bg-gray-700 border-gray-600'
                } text-gray-300`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    id="fileInput"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                />
                {files.length === 0 ? (
                    <p>Drag &amp; drop files here or click to select files</p>
                ) : (
                    <p>{files.length} file(s) selected</p>
                )}
            </div>

            <button
                type="button"
                onClick={uploadFiles}
                disabled={loading || files.length === 0}
                className="mt-3 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
            >
                {loading ? 'Uploading...' : 'Upload'}
            </button>
            {uploadedImages.length > 1 ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={uploadedImages.map((img, idx) => {
                            return img.id ? img.id.toString() : `image-${idx}`;
                        })}
                        strategy={rectSortingStrategy}
                    >
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {uploadedImages.map((img, index) => (
                                <SortableImage
                                    key={index}
                                    image={img}
                                    index={index}
                                    onRemove={handleRemoveImage}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            ) : uploadedImages.length === 1 ? (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="relative border p-2 bg-gray-700">
                        <img
                            src={
                                process.env.REACT_APP_STRAPI_URL
                                    ? process.env.REACT_APP_STRAPI_URL + uploadedImages[0].url
                                    : uploadedImages[0].url
                            }
                            alt="Uploaded-0"
                            className="object-cover w-full h-48"
                        />
                        <button
                            type="button"
                            className="absolute top-1 left-1 bg-red-600 text-white text-xs px-1 rounded"
                            onClick={() => handleRemoveImage(uploadedImages[0])}
                        >
                            X
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ImageUploader;
